import React from 'react';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ArrowLeft, CheckCircle2, Search } from 'lucide-react';
import { OOCIntegrationSummary, OOCForecastAdjustment, OOCForecastCell, OOCTabType, formatOOCNumber, formatOOCDate } from '../oocForecast.type';
import { getStatusLabel } from '../OOCDashboard/oocDashboard.logic';
import {
  useOOCHistoryLogic,
  useHistoryDetailLogic,
  TAB_LABELS,
  ROWS_PER_PAGE,
  HISTORY_CONTRACT_TYPE_OPTIONS,
  getDetailFcClass,
} from './oocHistory.logic';
import styles from './oocHistory.module.scss';

interface IntegrationCardProps {
  item: OOCIntegrationSummary;
}

const IntegrationCard: React.FC<IntegrationCardProps> = ({ item }) => (
  <div className={styles.integrationCard}>
    <div className={styles.integrationIconCircle}>
      <CheckCircle2 size={20} />
    </div>
    <div className={styles.integrationCardBody}>
      <div className={styles.integrationCardRow}>
        <span className={styles.integrationOocId}>{item.oocId}</span>
        <span className={styles.integrationDivider}>|</span>
        <span className={styles.integrationDate}>{item.date}</span>
        <span className={styles.integrationDivider}>|</span>
        <span className={styles.integrationSubmitter}>{item.submittedBy}</span>
      </div>
      <div className={styles.integrationCardRow}>
        <span className={styles.integrationStatusBadge}>
          {item.integratedIntoCycle ? `Integrated into ${item.integratedIntoCycle}` : getStatusLabel(item.status)}
        </span>
      </div>
      <div className={styles.integrationDetail}>
        <span className={styles.contractChips}>
          {item.contractTypes.map((ct) => (
            <span key={ct} className={styles.contractChip}>{TAB_LABELS[ct]}</span>
          ))}
        </span>
        {' '}· {item.ndcCount} NDCs · {item.reason}
      </div>
    </div>
  </div>
);

const HistoryDetailModal = observer(() => {
  const {
    historyDetailSubmission,
    isHistoryDetailLoading,
    closeHistoryDetail,
    visible,
    activeTab: _activeTab,
    setActiveTab,
    tabGroups,
    availableTabs,
    currentTab,
    currentAdjs,
    periods,
    showProbability,
  } = useHistoryDetailLogic();

  const renderFcCell = (cell: OOCForecastCell) => {
    if (cell.value === null) return <span className={styles.detailFcUnchanged}>-</span>;
    return (
      <div className={`${styles.detailFcValue} ${getDetailFcClass(cell.colorType, styles)}`}>
        {cell.previousValue !== null && cell.colorType === 'modified' && (
          <span className={styles.detailPrevValue}>was: {formatOOCNumber(cell.previousValue)}</span>
        )}
        {formatOOCNumber(cell.value)}
      </div>
    );
  };

  return (
    <Dialog
      header={historyDetailSubmission ? `${historyDetailSubmission.id} - OOC Detail` : 'Loading...'}
      visible={visible}
      onHide={closeHistoryDetail}
      className={styles.detailDialog}
      modal
      draggable={false}
      dismissableMask
    >
      {isHistoryDetailLoading ? (
        <div className={styles.loadingContainer}>
          <ProgressSpinner style={{ width: '40px', height: '40px' }} />
        </div>
      ) : historyDetailSubmission ? (
        <>
          <div className={styles.detailMeta}>
            {[
              { label: 'Reason', value: historyDetailSubmission.reason },
              { label: 'Submitted By', value: historyDetailSubmission.submittedBy },
              { label: 'Submission Date', value: formatOOCDate(historyDetailSubmission.submissionDate) },
              { label: 'Planning Period', value: historyDetailSubmission.planningPeriod },
              { label: 'Integrated Into', value: historyDetailSubmission.integratedIntoCycle ?? '-' },
            ].map(({ label, value }) => (
              <div key={label} className={styles.detailMetaItem}>
                <span className={styles.detailMetaLabel}>{label}</span>
                <span className={styles.detailMetaValue}>{value}</span>
              </div>
            ))}
            <div className={styles.detailMetaItem}>
              <span className={styles.detailMetaLabel}>Status</span>
              <span className={styles.statusBadgeIntegrated}>{getStatusLabel(historyDetailSubmission.status)}</span>
            </div>
          </div>

          <div className={styles.detailFcLegend}>
            <span className={styles.detailLegendLabel}>Color Key:</span>
            {[
              { color: '#d4edda', label: 'New FC' },
              { color: '#ffe0b2', label: 'Modified FC' },
              { color: '#ffcdd2', label: 'Removed FC' },
              { color: '#ffffff', label: 'Unchanged', bordered: true },
            ].map(({ color, label, bordered }) => (
              <span key={label} className={styles.detailLegendItem}>
                <span className={styles.detailLegendSwatch} style={{ backgroundColor: color, ...(bordered ? { border: '1px solid #ccc' } : {}) }} />
                {label}
              </span>
            ))}
          </div>

          {availableTabs.length > 0 && (
            <>
              <div className={styles.detailTabsRow}>
                {availableTabs.map((tab) => {
                  const count = tabGroups[tab]?.length ?? 0;
                  const isActive = currentTab === tab;
                  return (
                    <button
                      key={tab}
                      className={`${styles.detailTabButton} ${isActive ? styles.detailTabButtonActive : ''}`}
                      onClick={() => setActiveTab(tab)}
                    >
                      {TAB_LABELS[tab]}
                      <span className={`${styles.detailTabCount} ${isActive ? styles.detailTabCountActive : ''}`}>{count}</span>
                    </button>
                  );
                })}
              </div>

              {currentAdjs.length > 0 ? (
                <div className={styles.detailTableWrapper}>
                  <table className={styles.detailTable}>
                    <thead>
                      <tr>
                        <th>NDC10</th>
                        <th>NDC11</th>
                        <th>Supplier</th>
                        <th>Molecule</th>
                        <th>Concept</th>
                        <th>Pack Out</th>
                        {periods.map((p) => (
                          <th key={p} className={styles.detailFcCell}>{p}</th>
                        ))}
                        <th>Notes</th>
                        {showProbability && <th>Probability</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {currentAdjs.map((adj: OOCForecastAdjustment) => (
                        <tr key={adj.id}>
                          <td>{adj.ndc10}</td>
                          <td className={styles.detailColNdc11}>{adj.ndc11}</td>
                          <td>{adj.supplier}</td>
                          <td>{adj.molecule}</td>
                          <td>{adj.concept}</td>
                          <td>{adj.packOut}</td>
                          {adj.forecasts.map((cell, i) => (
                            <td key={i} className={styles.detailFcCell}>{renderFcCell(cell)}</td>
                          ))}
                          <td>{adj.notes || '-'}</td>
                          {showProbability && <td>{adj.probability ?? '-'}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className={styles.detailEmptyTab}>No adjustments in this tab.</div>
              )}
            </>
          )}
        </>
      ) : null}
    </Dialog>
  );
});

const OOCHistory: React.FC = () => {
  const {
    filteredHistoryData,
    isHistoryLoading,
    isSearching,
    navigateToDashboard,
    recentIntegrations,
    integratedCount,
    first,
    setFirst,
    visibleCount,
    handleRowClick,
    historyYear,
    pendingDate,
    setPendingDate,
    handleApplyFilter,
    handleShowYTD,
    handleClearFilter,
    selectedMonthLabel,
    searchInput,
    setSearchInput,
    contractTypeFilter,
    setContractTypeFilter,
  } = useOOCHistoryLogic();

  const periodLabel = historyYear === null
    ? 'All History'
    : selectedMonthLabel
      ? `${selectedMonthLabel} ${historyYear}`
      : `${historyYear} Year to Date`;

  if (isHistoryLoading) {
    return (
      <div className={styles.historyContainer}>
        <div className={styles.loadingContainer}>Loading history data...</div>
      </div>
    );
  }

  return (
    <div className={styles.historyContainer}>
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderRow}>
          <h2>OOC History &amp; DP Cycle Integrations</h2>
          <div className={styles.historyFilterRow}>
            <span className={styles.historyFilterLabel}>Filter by month:</span>
            <Calendar
              view="month"
              dateFormat="mm/yy"
              value={pendingDate}
              onChange={(e) => e.value instanceof Date && setPendingDate(e.value)}
              showIcon
              className={styles.historyMonthPicker}
            />
            <Button
              label="Apply"
              className={styles.historyFilterApplyBtn}
              onClick={handleApplyFilter}
              loading={isHistoryLoading}
            />
            {selectedMonthLabel !== null && (
              <Button
                label="Show Year to Date"
                className={`p-button-text ${styles.historyFilterClearBtn}`}
                onClick={handleShowYTD}
              />
            )}
            {historyYear !== null && (
              <Button
                label="Show All History"
                className={`p-button-text ${styles.historyFilterClearBtn}`}
                onClick={handleClearFilter}
              />
            )}
          </div>
        </div>
      </div>

      {/* ─── Search + filter toolbar ─────────────────────────────── */}
      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <Search size={16} className={styles.searchIcon} />
          <InputText
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by OOC ID, submitter, reason, NDC, molecule, or concept..."
            className={styles.searchInput}
          />
        </div>
        <Dropdown
          value={contractTypeFilter}
          options={HISTORY_CONTRACT_TYPE_OPTIONS}
          onChange={(e) => setContractTypeFilter(e.value as OOCTabType | 'all')}
          className={styles.filterDropdown}
          placeholder="All Contract Types"
        />
      </div>

      <div>
        <h3 className={styles.sectionTitle}>
          Recent DP Cycle Integrations — {periodLabel}
        </h3>
        <div className={styles.successCallout}>
          <CheckCircle2 size={16} className={styles.calloutIcon} />
          <span>
            {integratedCount} OOC submission{integratedCount !== 1 ? 's were' : ' was'} submitted in {periodLabel}.
          </span>
        </div>
        <div className={styles.integrationCardsGrid}>
          {recentIntegrations.map((item) => (
            <IntegrationCard key={item.oocId} item={item} />
          ))}
        </div>
      </div>

      <div className={styles.tableSection}>
        <h3 className={styles.sectionTitle}>
          OOC Submissions — {periodLabel}
        </h3>
        <div className={styles.tableWrapper}>
          {isSearching && (
            <div className={styles.searchingOverlay}>
              <ProgressSpinner style={{ width: '28px', height: '28px' }} />
              <span>Searching…</span>
            </div>
          )}
          <DataTable
            value={filteredHistoryData}
            paginator
            rows={ROWS_PER_PAGE}
            first={first}
            onPage={(e) => setFirst(e.first)}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
            selectionMode="single"
            emptyMessage="No OOC submissions match the selected filters."
            onSelectionChange={(e) => {
              const row = e.value as OOCIntegrationSummary;
              if (row) handleRowClick(row.oocId);
            }}
          >
            <Column
              field="oocId"
              header="OOC ID"
              body={(rowData: OOCIntegrationSummary) => (
                <span className={styles.oocIdCell} onClick={() => handleRowClick(rowData.oocId)}>
                  {rowData.oocId}
                </span>
              )}
              style={{ minWidth: '100px' }}
            />
            <Column field="date" header="Date" style={{ minWidth: '100px' }} />
            <Column field="submittedBy" header="Submitted By" style={{ minWidth: '140px' }} />
            <Column field="reason" header="Reason" style={{ minWidth: '180px' }} />
            <Column
              header="Contract Types"
              body={(rowData: OOCIntegrationSummary) => (
                <span className={styles.contractChips}>
                  {rowData.contractTypes.map((ct) => (
                    <span key={ct} className={styles.contractChip}>{TAB_LABELS[ct]}</span>
                  ))}
                </span>
              )}
              style={{ minWidth: '130px' }}
            />
            <Column field="ndcCount" header="# NDCs" style={{ minWidth: '70px', textAlign: 'center' }} />
            <Column
              header="Final Status"
              body={(rowData: OOCIntegrationSummary) => (
                <span className={styles.statusBadgeIntegrated}>{getStatusLabel(rowData.status)}</span>
              )}
              style={{ minWidth: '100px' }}
            />
            <Column
              field="integratedIntoCycle"
              header="Integrated Into"
              body={(rowData: OOCIntegrationSummary) => rowData.integratedIntoCycle || '—'}
              style={{ minWidth: '130px' }}
            />
          </DataTable>
        </div>
        <div className={styles.tableFooter}>Showing {visibleCount} of {filteredHistoryData.length}</div>
      </div>

      <div className={styles.actionBar}>
        <Button
          label="Back to Dashboard"
          icon={<ArrowLeft size={16} />}
          className={`p-button-text ${styles.backBtn}`}
          onClick={navigateToDashboard}
        />
      </div>

      <HistoryDetailModal />
    </div>
  );
};

export default observer(OOCHistory);
